/**
 * TanStack Query Hooks
 * 
 * Custom hooks for server state management with:
 * - Type-safe API calls
 * - Optimistic updates
 * - Cache invalidation
 * - Loading and error states
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../api/client';
import { queryKeys } from '../lib/query/keys';
import type {
  Colony,
  Brain,
  Thread,
  Message,
  KnowledgeDoc,
  User,
  ChatRequest,
  ChatResponse,
  KnowledgeAddRequest,
  KnowledgeDeleteRequest,
} from '../lib/types';
// Use the canonical Firebase auth service used across the app (frontend service)
import { firebaseAuthService } from '../../../frontend/services/firebaseAuth.service';

// User hooks
export function useProfile() {
  return useQuery({
    queryKey: queryKeys.user.profile(),
    queryFn: api.getProfile,
    staleTime: 10 * 60 * 1000, // Profile doesn't change often
  });
}

// Colony hooks
export function useColonies() {
  return useQuery({
    queryKey: queryKeys.colonies.list(),
    queryFn: api.getColonies,
    staleTime: 2 * 60 * 1000, // Colonies don't change often
  });
}

export function useColony(id: string) {
  return useQuery({
    queryKey: queryKeys.colonies.detail(id),
    queryFn: () => api.getColony(id),
    enabled: !!id,
  });
}

export function useCreateColony() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: api.createColony,
    onSuccess: (newColony) => {
      // Add to colonies list cache
      queryClient.setQueryData<Colony[]>(
        queryKeys.colonies.list(),
        (old) => old ? [...old, newColony] : [newColony]
      );
      
      // Invalidate colonies list to refetch
      queryClient.invalidateQueries({
        queryKey: queryKeys.colonies.list(),
      });
    },
  });
}

// Brain hooks
export function useBrains(colonyId: string) {
  return useQuery({
    queryKey: queryKeys.brains.list(colonyId),
    queryFn: () => api.getBrains(colonyId),
    enabled: !!colonyId,
  });
}

export function useCreateBrain() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (payload: { colonyId: string; name: string; personality: string; modelPreference: string; tone: 'professional' | 'casual' | 'friendly'; humor: 'none' | 'dry' | 'light' | 'witty'; specialties: string[]; color?: string }) => {
      const user = await firebaseAuthService.getCurrentUser();
      if (!user) throw new Error('Not authenticated');
      
      return api.createBrain(payload.colonyId, {
        name: payload.name,
        personality: payload.personality,
        modelPreference: payload.modelPreference,
        color: payload.color,
        tone: payload.tone,
        humor: payload.humor,
        specialties: payload.specialties,
        userId: user.uid,
      });
    },
    onSuccess: (newBrain, variables) => {
      // ✅ Invalidate colonies query to refetch and show updated brain count
      queryClient.invalidateQueries({ queryKey: queryKeys.colonies.all() });
      
      // Also, optimistically update the specific colony's brain list if needed
      queryClient.setQueryData<Brain[]>(
        queryKeys.brains.list(variables.colonyId),
        (old) => old ? [...old, newBrain] : [newBrain]
      );
    },
  });
}

export function useUpdateBrain(colonyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, brainId, persona }: { 
      userId: string; 
      brainId: string; 
      persona: Partial<Brain> 
    }) => {
      return api.updateBrain(colonyId, brainId, persona);
    },
    onSuccess: (updatedBrain, variables) => {
      // Invalidate and refetch colony data
      queryClient.invalidateQueries({ queryKey: queryKeys.colonies.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.colonies.detail(colonyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.brains.list(colonyId) });
      
      // Update specific brain cache
      queryClient.setQueryData(['brain-personality', colonyId, variables.brainId], updatedBrain);
    },
    onError: (error) => {
      console.error('Failed to update brain:', error);
    }
  });
}

// Custom hook to get a single brain's personality from the cache
export function useGetBrainPersonality(userId: string, colonyId: string, brainId: string) {
  return useQuery({
    queryKey: ['brain-personality', colonyId, brainId],
    queryFn: async () => {
      // Fetch colonies and find the specific brain
      const colonies = await api.getColonies();
      const colony = colonies.find(c => c.id === colonyId);
      if (!colony) {
        throw new Error('Colony not found');
      }
      
      const brain = colony.brains.find(b => b.id === brainId);
      if (!brain) {
        throw new Error('Brain not found');
      }
      
      // Return brain persona data in the format expected by EditBrain component
      return {
        id: brain.id,
        name: brain.name || '',
        personality: brain.personality || '',
        tone: brain.tone || 'professional',
        humor: brain.humor || 'none', 
        specialties: brain.specialties || [],
        // 'gemini-flash-life' was a typo for 'flash-lite'. normalizeModel()
        // matches on 'lite', so "life" fell through to the plain-Flash branch
        // and every brain without an explicit preference silently ran on the
        // dearer model. Spelling it correctly is a cost fix, not a rename.
        modelPreference: brain.modelPreference || 'gemini-flash-lite',
        color: brain.color || '#6366f1'
      } as Brain;
    },
    enabled: !!userId && !!colonyId && !!brainId && brainId !== 'alt-me',
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once for brain lookups
  });
}

export function useDeleteBrain(colonyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (brainId: string) => {
      if (!brainId || !colonyId) {
        throw new Error('Brain ID and Colony ID are required');
      }

      if (brainId === 'alt-me') {
        throw new Error('Cannot delete the Alt-me brain');
      }

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      try {
        console.log(`[useDeleteBrain] Deleting brain ${brainId} from colony ${colonyId}`);
        
        // Call your AWS Lambda endpoint
        const response = await api.delete(`/api/colony/${colonyId}/brain/${brainId}`, {
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        clearTimeout(timeoutId);
        console.log(`[useDeleteBrain] Successfully deleted brain ${brainId}`, response);
        return response;
        
      } catch (error: any) {
        clearTimeout(timeoutId);
        
        console.error(`[useDeleteBrain] Error deleting brain ${brainId}:`, error);
        
        // Handle specific error types
        if (error.name === 'AbortError') {
          throw new Error('Request timed out. Please check your connection and try again.');
        }
        
        if (error.response?.status === 403) {
          throw new Error('Permission denied. You can only delete brains you created.');
        }
        
        if (error.response?.status === 404) {
          throw new Error('Brain not found. It may have already been deleted.');
        }
        
        if (error.response?.status === 503) {
          const data = error.response?.data;
          if (data?.code === 'DYNAMODB_UNAVAILABLE') {
            throw new Error('Database temporarily unavailable. Please try again in a few moments.');
          }
          throw new Error('Service temporarily unavailable. Please try again later.');
        }
        
        if (error.response?.status === 500) {
          throw new Error('Server error occurred. Please try again.');
        }
        
        // Network or unknown errors
        if (!error.response) {
          throw new Error('Network error. Please check your internet connection.');
        }
        
        throw new Error(error.response?.data?.error || 'Failed to delete brain. Please try again.');
      }
    },
    
    onSuccess: (data, brainId) => {
      console.log(`[useDeleteBrain] Successfully deleted brain ${brainId}`);
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['brains', colonyId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.colonies.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.colonies.detail(colonyId) });
      
      // Optimistically remove from cache
      queryClient.setQueryData(['brains', colonyId], (oldData: any) => {
        if (!oldData || !Array.isArray(oldData)) return oldData;
        return oldData.filter((brain: any) => brain.id !== brainId);
      });
    },
    
    onError: (error: any, brainId) => {
      console.error(`[useDeleteBrain] Failed to delete brain ${brainId}:`, error);
    },
    
    // Retry configuration
    retry: (failureCount, error: any) => {
      if (error.response?.status >= 400 && error.response?.status < 500) {
        return false; // Don't retry client errors
      }
      return failureCount < 2; // Retry server errors up to 2 times
    },
    
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });
}

// Alt-me / Thread hooks
export function useAltMeThreads(colonyId: string) {
  return useQuery({
    queryKey: queryKeys.threads.altme(colonyId),
    queryFn: () => api.getAltMeThreads(colonyId),
    enabled: !!colonyId,
  });
}

export function useCreateAltMeThread(colonyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (title?: string) => api.createAltMeThread(colonyId, title),
    onSuccess: (newThread) => {
      // Add to threads list cache
      queryClient.setQueryData<Thread[]>(
        queryKeys.threads.altme(colonyId),
        (old) => old ? [...old, newThread] : [newThread]
      );
    },
  });
}

export function useMessages(threadId: string, cursor?: string) {
  return useQuery({
    queryKey: queryKeys.messages.list(threadId, cursor),
    queryFn: () => api.getMessages(threadId, cursor),
    enabled: !!threadId,
    staleTime: 30 * 1000, // Messages are relatively fresh
  });
}

export function useSendMessage(threadId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (content: string) => api.sendMessage(threadId, content),
    onMutate: async (content) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.messages.list(threadId),
      });

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData(queryKeys.messages.list(threadId));

      // Optimistically update to the new value
      const optimisticMessage: Message = {
        id: `temp-${Date.now()}`,
        threadId,
        sender: 'user',
        content,
        createdAt: new Date().toISOString(),
        metadata: { status: 'sending' },
      };

      queryClient.setQueryData(
        queryKeys.messages.list(threadId),
        (old?: { messages: Message[] }) => ({
          ...old,
          messages: old?.messages ? [...old.messages, optimisticMessage] : [optimisticMessage],
        })
      );

      return { previousMessages };
    },
    onError: (err, content, context) => {
      // Rollback on error
      if (context?.previousMessages) {
        queryClient.setQueryData(queryKeys.messages.list(threadId), context.previousMessages);
      }
    },
    onSuccess: (newMessage) => {
      // Replace optimistic update with real message
      queryClient.setQueryData(
        queryKeys.messages.list(threadId),
        (old?: { messages: Message[] }) => ({
          ...old,
          messages: old?.messages ? 
            [...old.messages.filter((m: Message) => !m.id.startsWith('temp-')), newMessage] : 
            [newMessage],
        })
      );
    },
  });
}

// Chat hooks (existing endpoint)
export function useSendChatMessage() {
  return useMutation({
    mutationFn: (request: ChatRequest) => api.sendChatMessage(request),
  });
}

// Knowledge hooks
export function useKnowledge(colonyId: string) {
  return useQuery({
    queryKey: queryKeys.knowledge.list(colonyId),
    queryFn: () => api.listKnowledge(colonyId),
    enabled: !!colonyId,
  });
}

export function useAddKnowledge(colonyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (request: KnowledgeAddRequest) => api.addKnowledge(request),
    onSuccess: (response, request) => {
      // Optimistically add to knowledge list
      const newDoc: KnowledgeDoc = {
        docId: response.docId,
        colonyId: request.colonyId,
        name: request.title,
        type: request.mime || 'text/plain',
        size: request.text.length,
        uploadedAt: new Date().toISOString(),
        indexingStatus: 'pending',
        jobId: response.jobId,
      };

      queryClient.setQueryData(
        queryKeys.knowledge.list(colonyId),
        (old?: { items: KnowledgeDoc[] }) => ({
          ...old,
          items: old?.items ? [...old.items, newDoc] : [newDoc],
        })
      );
    },
  });
}

export function useDeleteKnowledge(colonyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (request: KnowledgeDeleteRequest) => api.deleteKnowledge(request),
    onSuccess: (_, request) => {
      // Remove from knowledge list cache
      queryClient.setQueryData(
        queryKeys.knowledge.list(colonyId),
        (old?: { items: KnowledgeDoc[] }) => ({
          ...old,
          items: old?.items ? old.items.filter((item: KnowledgeDoc) => item.docId !== request.docId) : [],
        })
      );
    },
  });
}

// Job status hooks (for indexing progress)
export function useJobStatus(jobId: string) {
  return useQuery({
    queryKey: queryKeys.jobs.detail(jobId),
    queryFn: () => api.getJob(jobId),
    enabled: !!jobId,
    refetchInterval: (data: any) => {
      // Stop polling when job is complete
      return data?.status === 'completed' || data?.status === 'failed' ? false : 2000;
    },
  });
}

// Feedback hooks
export function useSendFeedback() {
  return useMutation({
    mutationFn: api.sendFeedback,
  });
}

// Traces hooks
export function useTraces() {
  return useQuery({
    queryKey: queryKeys.traces.list(),
    queryFn: api.getTraces,
    staleTime: 60 * 1000, // Traces are relatively static
  });
}
